import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ImagePlus, UserPlus, Loader2, Lock } from "lucide-react";
import { uploadToCloudinary } from "@/lib/cloudinary";

// Ampliación de evidencias (NO es resetear montaje): oficina añade fotos o habilita al montador a añadir más.
export function contarOrigenFotos(fotos) {
  const c = { montador: 0, oficina: 0 };
  (fotos || []).forEach((f) => { c[f?.origen === "oficina" ? "oficina" : "montador"] += 1; });
  return c;
}

export function FotosOficinaActions({ reporte, onChange }) {
  const p = reporte.parada || {};
  const fileRef = useRef(null);
  const [subiendo, setSubiendo] = useState(false);
  const [pendientes, setPendientes] = useState([]);
  const [motivoOpen, setMotivoOpen] = useState(false);
  const [motivo, setMotivo] = useState("");
  const amp = p.ampliacion_evidencias || {};
  const bloqueado = p.validacion?.estado === "aprobado" || !!p.enviado_cliente_at || !!p.reporte_finalizado_at;

  const enviar = async (files, reabrir = false) => {
    setSubiendo(true);
    try {
      const fotos = [];
      for (const file of files) {
        const up = await uploadToCloudinary(file, "dnf/montajes", "image");
        fotos.push({ url: up.secure_url, public_id: up.public_id, categoria: "libre", tipo: "foto", mime: file.type, size_bytes: up.bytes ?? file.size });
      }
      const body = reabrir ? { fotos, reabrir_validacion: true, motivo } : { fotos };
      const { data } = await api.post(`/rutas/${reporte.ruta_id}/paradas/${reporte.idx}/fotos-oficina`, body);
      toast.success(`${data.fotos.length} foto(s) añadidas al reporte${data.validacion_reabierta ? " · validación reabierta" : ""}`);
      setMotivoOpen(false); setMotivo(""); setPendientes([]);
      onChange && (await onChange());
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.requiere_reapertura) { setPendientes(files); setMotivoOpen(true); }
      else toast.error(d?.msg || d || e.message || "Error añadiendo fotos");
    } finally { setSubiendo(false); }
  };

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (bloqueado) { setPendientes(files); setMotivoOpen(true); return; }
    enviar(files);
  };

  const toggleAmpliacion = async () => {
    try {
      const { data } = await api.post(`/rutas/${reporte.ruta_id}/paradas/${reporte.idx}/ampliacion-evidencias`, { abrir: !amp.abierta });
      toast.success(data.ampliacion_evidencias?.abierta ? "El montador ya puede añadir más fotos desde Mi Ruta" : "Ampliación cerrada");
      onChange && (await onChange());
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.msg || d || "No se pudo cambiar la ampliación");
    }
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="fotos-oficina-actions">
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid="fotos-oficina-input" />
      <Button size="sm" variant="outline" disabled={subiendo} onClick={() => fileRef.current?.click()} data-testid="fotos-oficina-btn"
        className="h-7 text-xs bg-transparent border-amber-500/40 text-amber-300 hover:bg-amber-500/10">
        {subiendo ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : bloqueado ? <Lock className="w-3.5 h-3.5 mr-1" /> : <ImagePlus className="w-3.5 h-3.5 mr-1" />}
        Añadir fotos
      </Button>
      <Button size="sm" variant="outline" onClick={toggleAmpliacion} disabled={bloqueado && !amp.abierta} data-testid="ampliacion-toggle-btn"
        title={bloqueado && !amp.abierta ? "Reporte aprobado/enviado: reabre la validación añadiendo fotos con motivo" : ""}
        className={`h-7 text-xs bg-transparent ${amp.abierta ? "border-sky-500/60 text-sky-200 bg-sky-500/10" : "border-zinc-700 text-zinc-300"} hover:bg-sky-500/10`}>
        <UserPlus className="w-3.5 h-3.5 mr-1" /> {amp.abierta ? "Montador añadiendo fotos · cerrar" : "Pedir más fotos al montador"}
      </Button>
      {amp.abierta && <span className="text-[10px] text-sky-300" data-testid="ampliacion-abierta-badge">Ampliación abierta por {amp.solicitada_por}</span>}
      {!amp.abierta && amp.cerrada_at && amp.fotos_anadidas != null && <span className="text-[10px] text-zinc-500">Montador añadió {amp.fotos_anadidas} foto(s)</span>}

      <Dialog open={motivoOpen} onOpenChange={setMotivoOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md" data-testid="reabrir-validacion-dialog">
          <DialogHeader><DialogTitle>Reporte ya aprobado / enviado</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-400">Para añadir {pendientes.length} foto(s) hay que <b>reabrir la validación</b>. El reporte volverá a "Pendiente de validar" y quedará registrado en la auditoría. No se borra ni modifica nada anterior.</p>
          <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo (obligatorio)…" className="bg-zinc-900 border-zinc-800" data-testid="reabrir-validacion-motivo" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setMotivoOpen(false); setPendientes([]); }}>Cancelar</Button>
            <Button size="sm" disabled={!motivo.trim() || subiendo} onClick={() => enviar(pendientes, true)} data-testid="reabrir-validacion-confirmar"
              className="bg-amber-500 text-zinc-900 hover:bg-amber-400">Reabrir y añadir fotos</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
